// Modal that the "Buy License(s)" buttons open. Org member specifies how
// many additional seats they want + an optional context note. Backend logs
// the request; an admin actions it by granting seats via the admin app.
//
// Used by:
//   - DashboardPage   → "Buy License" CTA in the licences card
//   - ScreenBuilderPage → "Buy Licenses" CTA above the pairing-code input
//
// The form deliberately doesn't take money — payment is offline today
// (manual SAR transfer) and licenses are activated by an admin.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2, KeyRound } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@meno/ui/components/ui/dialog'
import { Button } from '@meno/ui/components/ui/button'
import { Label } from '@meno/ui/components/ui/label'
import { Textarea } from '@meno/ui/components/ui/textarea'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@meno/ui/components/ui/select'
import { requestAppLicenses } from '@meno/ui/lib/api'

interface Props {
  open:     boolean
  onClose:  () => void
  appKind?: string          // defaults to 'signage'
  appLabel?: string         // human-readable, shown in title
}

// Fixed set of quantities — a dropdown, not a free-typed number. Every value
// here is inherently valid, so there's no client-side range validation left
// to do (the old 1..1000 text-input check is gone with the text input). Capped
// at 15 — a bigger ask goes through the admin directly rather than this form.
const QUANTITY_OPTIONS = [1, 2, 3, 4, 5, 10, 15]

export default function LicenseRequestDialog({
  open, onClose, appKind = 'signage', appLabel,
}: Props) {
  const { t } = useTranslation()
  const [quantity, setQuantity] = useState('1')
  const [reason,   setReason]   = useState('')
  const [submitting, setSubmitting] = useState(false)

  const qty       = parseInt(quantity, 10)
  const canSubmit = !submitting

  const resetAndClose = () => {
    setQuantity('1')
    setReason('')
    onClose()
  }

  const submit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      const { data } = await requestAppLicenses(appKind, {
        quantity: qty,
        reason: reason.trim() || undefined,
      })
      toast.success(data.message || t('licenseRequest.submitted', 'Request submitted.'))
      resetAndClose()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('licenseRequest.failed', 'Could not submit your request. Try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  const label = appLabel ?? t('licenseRequest.defaultAppLabel', 'Frame')

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) resetAndClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-primary" />
            {t('licenseRequest.title', { defaultValue: 'Request {{app}} licenses', app: label })}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="license-quantity">{t('licenseRequest.quantity', 'How many additional licenses?')}</Label>
            <Select value={quantity} onValueChange={setQuantity}>
              <SelectTrigger id="license-quantity" autoFocus>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUANTITY_OPTIONS.map(n => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="license-reason">
              {t('licenseRequest.reason', 'Context (optional)')}
            </Label>
            <Textarea
              id="license-reason"
              value={reason}
              onChange={e => setReason(e.target.value.slice(0, 2000))}
              placeholder={t(
                'licenseRequest.reasonPlaceholder',
                'e.g. opening 5 new branches next month',
              )}
              rows={3}
            />
            <p className="text-[11px] text-muted-foreground text-end">{reason.length} / 2000</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={resetAndClose} disabled={submitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={!canSubmit} className="gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('licenseRequest.submit', 'Submit request')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
